/**
 * Application Entry Point
 * Uses hydrateRoot when pre-rendered content matches the current route.
 * Falls back to createRoot for non-prerendered routes (e.g. product pages
 * served via the SPA fallback index.html).
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { App } from './App';
import { ServerDataProvider, type ServerData } from './lib/server-data';
import './styles/globals.css';

// Read server data embedded by prerender script
function getServerData(): ServerData {
  try {
    const el = document.getElementById('__SERVER_DATA__');
    if (el?.textContent) {
      return JSON.parse(el.textContent);
    }
  } catch {
    // Ignore parse errors
  }
  return {};
}

const root = document.getElementById('root')!;
const serverData = getServerData();

// Only hydrate if the pre-rendered HTML is for the current route.
// When a non-prerendered route (e.g. /products/some-handle) falls back to
// index.html, it gets homepage HTML — hydrating that as a product page
// causes React errors #418/#423.
const hasPrerenderedContent = root.innerHTML.trim().length > 0;
const routeMatches = serverData.routePath === window.location.pathname;
const shouldHydrate = hasPrerenderedContent && routeMatches;

if (shouldHydrate) {
  ReactDOM.hydrateRoot(
    root,
    <React.StrictMode>
      <ServerDataProvider value={serverData}>
        <App />
      </ServerDataProvider>
    </React.StrictMode>
  );
} else {
  // Clear any mismatched pre-rendered content before client render
  if (hasPrerenderedContent && !routeMatches) {
    root.innerHTML = '';
  }
  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <ServerDataProvider value={{}}>
        <App />
      </ServerDataProvider>
    </React.StrictMode>
  );
}
